import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from './AuthContext';
import { effectiveConditions, isLibraryItemAllowed, isRecipeAllowed } from '../data/profileOptions';

const ACTIVE_KEY = 'botanica_active_profile';
const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(ACTIVE_KEY) || null);

  const refresh = useCallback(async () => {
    if (!user) { setProfiles([]); return; }
    try {
      const res = await axios.get(`${API}/profiles`);
      setProfiles(res.data.profiles || []);
    } catch (e) {
      setProfiles([]);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const setActive = useCallback((id) => {
    setActiveId(id);
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  }, []);

  const create = useCallback(async (payload) => {
    const res = await axios.post(`${API}/profiles`, payload);
    await refresh();
    return res.data;
  }, [refresh]);

  const update = useCallback(async (id, payload) => {
    const res = await axios.put(`${API}/profiles/${id}`, payload);
    await refresh();
    return res.data;
  }, [refresh]);

  const remove = useCallback(async (id) => {
    await axios.delete(`${API}/profiles/${id}`);
    if (activeId === id) setActive(null);
    await refresh();
  }, [activeId, refresh, setActive]);

  const activeProfile = useMemo(
    () => profiles.find((p) => p.profile_id === activeId) || null,
    [profiles, activeId]
  );

  const activeConditions = useMemo(
    () => effectiveConditions(activeProfile, profiles),
    [activeProfile, profiles]
  );

  const filterLibrary = useCallback(
    (items) => (activeProfile ? items.filter((i) => isLibraryItemAllowed(i, activeConditions)) : items),
    [activeProfile, activeConditions]
  );

  const filterRecipes = useCallback(
    (recipes) => (activeProfile ? recipes.filter((r) => isRecipeAllowed(r, activeProfile)) : recipes),
    [activeProfile]
  );

  return (
    <ProfileContext.Provider
      value={{ profiles, activeProfile, activeId, activeConditions, setActive, refresh, create, update, remove, filterLibrary, filterRecipes }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfiles() {
  return useContext(ProfileContext);
}
